import { useEffect, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { buildApiUrl, fetchAPI, useUserStore } from "@neu/libui";

const INSTALL_ID_KEY = "neu_web_push_client_installation_id";

function readFirebaseWebConfig(): {
	apiKey: string;
	authDomain: string;
	projectId: string;
	storageBucket: string;
	messagingSenderId: string;
	appId: string;
} {
	return {
		apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? "",
		authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? "",
		projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? "",
		storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? "",
		messagingSenderId:
			import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? "",
		appId: import.meta.env.VITE_FIREBASE_APP_ID ?? "",
	};
}

function getClientInstallationID(): string {
	try {
		let id = localStorage.getItem(INSTALL_ID_KEY);
		if (!id) {
			id = crypto.randomUUID();
			localStorage.setItem(INSTALL_ID_KEY, id);
		}
		return id;
	} catch {
		return `web-${Date.now()}`;
	}
}

function isConfigComplete(
	cfg: ReturnType<typeof readFirebaseWebConfig>
): boolean {
	return Boolean(
		cfg.apiKey &&
		cfg.authDomain &&
		cfg.projectId &&
		cfg.messagingSenderId &&
		cfg.appId
	);
}

function pushDataValueToString(v: unknown): string | null {
	if (typeof v === "string") {
		return v;
	}
	if (typeof v === "number" || typeof v === "boolean") {
		return String(v);
	}
	return null;
}

function handleChatPushPayload(
	queryClient: ReturnType<typeof useQueryClient>,
	data: Record<string, string> | undefined
): void {
	const type = data?.type;
	if (type !== "chat_message") {
		return;
	}
	const roomID = data?.room_id;
	void queryClient.invalidateQueries({ queryKey: ["chat", "rooms"] });
	if (roomID) {
		void queryClient.invalidateQueries({
			queryKey: ["chat", "rooms", roomID, "messages"],
		});
		void queryClient.invalidateQueries({
			queryKey: ["chat", "rooms", roomID, "detail"],
		});
	}
}

/**
 * Registers the browser FCM token after login and refreshes TanStack Query chat
 * caches when a chat push arrives (foreground via onMessage, background via SW postMessage).
 *
 * Requires VITE_FIREBASE_* and VITE_FIREBASE_VAPID_KEY; `/firebase-messaging-sw.js` is emitted at build and served in dev from env.
 */
export function ChatPushSync() {
	const queryClient = useQueryClient();
	const loggedIn = useUserStore((s) => s.loggedIn);
	const unsubRef = useRef<(() => void) | null>(null);

	useEffect(() => {
		if (typeof window === "undefined" || !loggedIn) {
			return () => {};
		}

		const cfg = readFirebaseWebConfig();
		const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY ?? "";
		if (!isConfigComplete(cfg) || !vapidKey.trim()) {
			return () => {};
		}

		let cancelled = false;

		const onServiceWorkerMessage = (event: MessageEvent) => {
			if (event.data?.type !== "FIREBASE_MESSAGE") {
				return;
			}
			const raw = event.data.payload?.data as
				| Record<string, unknown>
				| undefined;
			if (!raw) {
				return;
			}
			const data: Record<string, string> = {};
			for (const [k, v] of Object.entries(raw)) {
				const s = pushDataValueToString(v);
				if (s !== null) {
					data[k] = s;
				}
			}
			handleChatPushPayload(queryClient, data);
		};

		navigator.serviceWorker?.addEventListener(
			"message",
			onServiceWorkerMessage
		);

		void (async () => {
			const [{ initializeApp, getApps }, messagingMod] =
				await Promise.all([
					import("firebase/app"),
					import("firebase/messaging"),
				]);

			const { getMessaging, getToken, isSupported, onMessage } =
				messagingMod;

			if (!(await isSupported())) {
				return;
			}

			const app = getApps().length ? getApps()[0] : initializeApp(cfg);
			const messaging = getMessaging(app);

			if (Notification.permission === "default") {
				await Notification.requestPermission();
			}
			if (Notification.permission !== "granted") {
				return;
			}

			let registrationToken: string;
			try {
				registrationToken = await getToken(messaging, {
					vapidKey: vapidKey.trim(),
					serviceWorkerRegistration:
						await navigator.serviceWorker.register(
							"/firebase-messaging-sw.js",
							{ scope: "/" }
						),
				});
			} catch {
				return;
			}

			if (cancelled || !registrationToken) {
				return;
			}

			try {
				await fetchAPI(buildApiUrl("/me/push-devices/register"), {
					method: "POST",
					body: JSON.stringify({
						registrationToken,
						pushProvider: "fcm",
						platform: "web",
						clientInstallationId: getClientInstallationID(),
					}),
				});
			} catch {
				return;
			}

			if (cancelled) {
				return;
			}

			unsubRef.current = onMessage(messaging, (payload) => {
				const raw = payload.data as Record<string, unknown> | undefined;
				if (!raw) {
					return;
				}
				const data: Record<string, string> = {};
				for (const [k, v] of Object.entries(raw)) {
					const s = pushDataValueToString(v);
					if (s !== null) {
						data[k] = s;
					}
				}
				handleChatPushPayload(queryClient, data);
			});
		})();

		return () => {
			cancelled = true;
			navigator.serviceWorker?.removeEventListener(
				"message",
				onServiceWorkerMessage
			);
			unsubRef.current?.();
			unsubRef.current = null;
		};
	}, [loggedIn, queryClient]);

	return null;
}
